/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import "./App.css";
import { DatePicker, Table, Tag, Input, Typography } from "antd";
import "antd/dist/reset.css";
import dayjs, { Dayjs } from "dayjs";
import { useIsMobile } from "./hooks/useIsMobile";
import { useS3Data } from "./hooks/useS3Data";
import { getClosestPastWeekday } from "./StockReport";
import { DartArticle } from "./types";

interface Props {
  type: 'dart' | 'kind';
  searchForTotalDart: string;
}

const CONFIG = {
  dart: {
    title: 'Dart 공시',
    description: '8.26 이후부터 데이터 조회가능',
    companyCountThreshold: 3,
  },
  kind: {
    title: 'Kind 공시',
    description: '9.3 이후부터 데이터 조회가능',
    companyCountThreshold: 2,
  },
};

const DisclosureViewer: React.FC<Props> = ({ type, searchForTotalDart }) => {
  const isMobile = useIsMobile();
  const config = CONFIG[type];
  const [datesForDart, setDatesForDart] = useState(getClosestPastWeekday(dayjs()));
  const [keywordFilterForDart, setKeywordFilterForDart] = useState<string>();

  const dateString = datesForDart ? datesForDart.format("YYYY.MM.DD") : null;
  const s3Key = dateString ? `data/${type}-${dateString}.json` : '';
  const { data: dartArticles = [], isLoading: loading } = useS3Data<DartArticle[]>(s3Key, !!dateString);

  useEffect(() => {
    setKeywordFilterForDart(undefined);
  }, [datesForDart]);

  const companyCount = dartArticles.reduce((acc, obj) => {
    acc[obj.company] = (acc[obj.company] || 0) + 1;
    return acc;
  }, {} as { [key: string]: number });

  const dartResults = dartArticles
    .filter((article) => {
      if (keywordFilterForDart) {
        const lowerKeyword = keywordFilterForDart.toLowerCase();
        return (
          article.title.toLowerCase().includes(lowerKeyword) ||
          article.company.toLowerCase().includes(lowerKeyword)
        );
      }
      return true;
    })
    .filter((article) => {
      if (searchForTotalDart) {
        return (
          article.title.toLowerCase().includes(searchForTotalDart.toLowerCase()) ||
          article.company.toLowerCase().includes(searchForTotalDart.toLowerCase())
        );
      }
      return true;
    });

  const renderTitleCell = (text: string, record: DartArticle) => {
    if (type === 'kind') {
      return (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a
            href={record.link}
            onClick={(e) => {
              e.preventDefault();
              window.open(record.link, "popupWindow", "width=1000,height=800,scrollbars=yes,resizable=yes");
            }}
          >
            {text}
          </a>
          {record.type ? <Tag>{record.type}</Tag> : <div />}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <a href={record.link} target="_blank" rel="noopener noreferrer">{text}</a>
        {record.type ? <Tag>{record.type}</Tag> : <div />}
      </div>
    );
  };

  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16 }}>
        <Typography.Title level={5} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 0 }}>
          {config.title}
        </Typography.Title>
        <Typography.Text style={{ color: "gray" }}>
          {config.description}
        </Typography.Text>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 20 }}>
        <DatePicker
          value={datesForDart}
          onChange={(e) => { setDatesForDart(e); }}
        />
        <Input.Search
          value={keywordFilterForDart}
          onChange={(e) => { setKeywordFilterForDart(e.target.value); }}
          style={{ width: 300 }}
          placeholder="검색어를 입력하세요 (회사,제목)"
        />
      </div>
      <Table
        loading={loading}
        size="small"
        columns={[
          {
            title: "회사",
            dataIndex: "company",
            width: isMobile ? 100 : 150,
            sorter: (a: DartArticle, b: DartArticle) => a.company.localeCompare(b.company),
          },
          {
            title: "제목",
            dataIndex: "title",
            render: renderTitleCell,
          },
          {
            title: "시간",
            dataIndex: "time",
            sorter: (a: DartArticle, b: DartArticle) => a.time.localeCompare(b.time),
          },
        ]}
        dataSource={dartResults}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          pageSize: 20,
        }}
      />
    </div>
  );
};

export default DisclosureViewer;
